import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  Quiz,
  Assessment,
  PaginatedResponse,
  ResponseValue,
} from '../types';
import { SessionTokenResponse, ApiErrorResponse } from '../types/hooks';

const apiUrl = import.meta.env.VITE_API_URL ?? '';

const apiClient: AxiosInstance = axios.create({
  baseURL: apiUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add user identity headers to requests for API auth
apiClient.interceptors.request.use((config) => {
  if ((config.headers['X-Skip-Auth'] || config.headers['x-skip-auth']) === 'true') {
    delete config.headers['X-Skip-Auth'];
    delete config.headers['x-skip-auth'];
    return config;
  }
  // Add identity headers (from MSAL or mock auth)
  const userId = sessionStorage.getItem('user_id');
  const userEmail = sessionStorage.getItem('user_email');
  const tenantId = sessionStorage.getItem('tenant_id');
  const userRoles = sessionStorage.getItem('user_roles');

  if (userId && userEmail && tenantId) {
    config.headers['x-user-id'] = userId;
    config.headers['x-user-email'] = userEmail;
    config.headers['x-tenant-id'] = tenantId;
    if (userRoles) {
      config.headers['x-user-roles'] = userRoles;
    }
  }

  // Add assessment session token if present
  const sessionToken = sessionStorage.getItem('assessment_session_token');
  if (sessionToken && !config.headers['X-Session-Token']) {
    config.headers['X-Session-Token'] = sessionToken;
  }

  return config;
});

// Handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only treat as session expiry if the request actually had user auth headers.
      // Public endpoints (e.g. POST /Assessments) legitimately return 401 without
      // implying the advisor's session is invalid.
      const hadUserSession = Boolean(error.config?.headers?.['x-user-id']);
      if (hadUserSession) {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('user_id');
        sessionStorage.removeItem('user_email');
        sessionStorage.removeItem('tenant_id');
        sessionStorage.removeItem('user_roles');
        sessionStorage.removeItem('assessment_session_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Fetch a quiz by ID
 * @param quizId - The quiz ID
 * @returns Promise resolving to Quiz
 * @throws ApiErrorResponse on error
 */
export const getQuiz = async (quizId: number): Promise<Quiz> => {
  try {
    const response = await apiClient.get<{ data: Quiz }>(
      `/api/v1/Quizzes/${quizId}`,
    );
    return response.data.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Create a session token for taking a quiz
 * @param prospectEmail - Prospect's email address
 * @param quizId - The quiz ID
 * @returns Promise resolving to { token, expiresAt }
 * @throws ApiErrorResponse on error
 */
export const createSessionToken = async (
  prospectEmail: string | null,
  quizId: number,
): Promise<SessionTokenResponse> => {
  try {
    const response = await apiClient.post<{ data: SessionTokenResponse }>(
      '/api/v1/Assessments/session',
      { prospectEmail, quizId },
    );
    return response.data.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Submit assessment responses and get scoring
 * @param quizId - The quiz ID
 * @param prospectEmail - Prospect's email
 * @param responses - Map of question IDs to answer IDs
 * @param sessionToken - Session token from createSessionToken
 * @returns Promise resolving to Assessment with scores
 * @throws ApiErrorResponse on error
 */
export const submitAssessment = async (
  quizId: number,
  prospectEmail: string | null,
  responses: Record<number, ResponseValue>,
  sessionToken: string,
): Promise<Assessment> => {
  try {
    const config = {
      headers: {
        'X-Session-Token': sessionToken,
      },
    };

    const response = await apiClient.post<{ data: Assessment }>(
      '/api/v1/Assessments',
      { quizId, prospectEmail, responses },
      config,
    );
    return response.data.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get a previously submitted assessment
 * @param assessmentId - The assessment ID
 * @param token - Optional session token or auth token
 * @returns Promise resolving to Assessment
 * @throws ApiErrorResponse on error
 */
export const getAssessment = async (
  assessmentId: number,
  token?: string,
): Promise<Assessment> => {
  try {
    const config = token
      ? {
          headers: {
            'X-Session-Token': token,
          },
        }
      : undefined;

    const response = await apiClient.get<{ data: Assessment }>(
      `/api/v1/Assessments/${assessmentId}`,
      config,
    );
    return response.data.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List assessments with optional filtering
 * @param filters - Filter criteria (quizId, prospectEmail, etc.)
 * @returns Promise resolving to paginated assessments
 * @throws ApiErrorResponse on error
 */
export const listAssessments = async (
  filters?: Record<string, any>,
): Promise<PaginatedResponse<Assessment>> => {
  try {
    const response = await apiClient.get<{ data: PaginatedResponse<Assessment> }>(
      '/api/v1/Assessments',
      { params: filters },
    );
    return response.data.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Export assessments as CSV
 * @param filters - Filter criteria
 * @returns Promise resolving to CSV string
 * @throws ApiErrorResponse on error
 */
export const exportAssessmentsCSV = async (
  filters?: Record<string, any>,
): Promise<string> => {
  try {
    const response = await apiClient.get<string>(
      '/api/v1/Assessments/export',
      { params: filters, headers: { Accept: 'text/csv' } },
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Handle API errors consistently
 * @param error - Axios error
 * @returns ApiErrorResponse
 */
function handleApiError(error: unknown): ApiErrorResponse {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ error: ApiErrorResponse }>;
    const status = axiosError.response?.status || 500;
    const errorData = axiosError.response?.data?.error;

    return {
      status,
      code: errorData?.code || 'UNKNOWN_ERROR',
      message: errorData?.message || axiosError.message || 'An error occurred',
      details: errorData?.details,
    };
  }

  return {
    status: 500,
    code: 'UNKNOWN_ERROR',
    message: error instanceof Error ? error.message : 'An unknown error occurred',
  };
}

export default apiClient;
